/* eslint-disable no-underscore-dangle */
import { actionCreators } from "react/api/actions/monitor";
import { actionCreators as actionsModals } from "react/api/actions/modals";
import { getResourceTo } from "./utils";

const getStopsByRestrictions = (routeId) =>
  new Promise((resolve) => {
    const stopCollection = new App.Collections.StopViews();

    const filter = {
      criteriaChain: [{ and: [{ attr: "route.id", eq: routeId }] }],
      sort: [{ attr: "stop.plannedSequenceNum", type: "ASC" }],
    };
    const projection = {
      filters: [
        "id",
        "stop.location.id",
        "stop.location.key",
        "stop.actualService",
        "stop.location.description",
        "stop.id",
        "stop.location.city",
        "stop.baseLineSequenceNum",
        "stop.plannedSequenceNum",
        "stop.actualSequenceNum",
        "stop.redeliveryStop.location.key",
        "stop.cancelCode.key",
        "stop.undeliverableCode.id",
        "stop.undeliverableCode.key",
        "stop.location.locationType.id",
        "stop.location.locationType.description",
        "stop.projectedArrival",
        "stop.actualArrival",
        "stop.plannedService",
        "stop.projectedService",
        "stop.baseLineDeparture",
        "stop.plannedDeparture",
        "stop.projectedDeparture",
        "stop.actualDeparture",
        "stop.baseLineDistance",
        "stop.plannedDistance",
        "stop.projectedDistance",
        "stop.actualDistance",
        "stop.deliveryStatus",
        "stop.latitude",
        "stop.longitude",
        "stop.location.latitude",
        "stop.location.longitude",
        "stop.location.locationType.*",
        "stop.stopType.*",
        "stop.cancelCode.*",
        "stop.redeliveryStop.*",
        "stop.redeliveryStop.location.key*",
        "stop.undeliverableCode.*",
        "stop.actualCancel",
        "stop.confirmedAsNextStop",
        "route.origin.id",
        "route.destination.id",
        "route.origLatitude",
        "route.origLongitude",
        "route.destLatitude",
        "route.destLongitude",
        "route.destination.organization.*",
        "route.destination.locationType.*",
        "route.proactiveRouteOptConfig",
      ],
    };

    stopCollection.filterWithRestrictions({
      filter,
      criteria: JSON.stringify(projection),
      success: (result) => {
        resolve(result);
      },
    });
  });

const getEncodedPath = (routeId) =>
  new Promise((resolve) => {
    const url = `Route/${routeId}/EncodedPath?lastUpdate=${new Date().toJSON()}`;

    $.get(url, (encoded) => {
      const decodedPath = new Monet.PolylineEncoder().decode(encoded, false);

      resolve(decodedPath);
    });
  });

const generateStopMarkers = ({ routeId, stops, color }) => {
  const mappedColors = {
    purple: 'var(--stop-marker-purple)',
    green: 'var(--stop-marker-green)',
    red: 'var(--stop-marker-red)',
    blue: 'var(--stop-marker-blue)',
    orange: 'var(--stop-marker-orange)'
  };

  const layer = L.layerGroup([]);

  const markerStops = new Greenmile.RouteDetail.MarkerStop(layer, routeId);
  markerStops.baseColor = mappedColors[color];
  markerStops.clearBeforeDraw = false;
  markerStops.setDrawGeocode(false);
  markerStops.onStopClick = (data) => {
    window.GreenmileReduxStore.dispatch(
      actionsModals.openModal({
        modalKey: "shortStopDetails",
        title: getResourceTo("modal.stopdetails.title"),
        data,
      }),
    );
  };

  markerStops.drawStopsMarkers(stops, null);

  return { layer, points: markerStops.points };
};

const getRoutePathLayer = ({ path, color } = {}) => {
  const mappedColors = {
    purple: '#7B3280',
    green: '#4D9A3A',
    red: '#BA110E',
    blue: '#007BB7',
    orange: '#D96D00'
  };

  const polyline = L.polyline(path, {
    color: mappedColors[color],
    weight: 4,
    opacity: 0.8,
  });
  polyline.kind = "PLANNED_ROUTE";

  polyline.on("mouseover", (e) => {
    const mappedHoverredColors = {
      purple: "#8B4B90",
      green: "#63A652",
      red: "#C22E2B",
      blue: "#1F8BC0",
      orange: "#DE7F1F",
    };

    const hoveredColor = mappedHoverredColors[color];

    if (hoveredColor) {
      e.sourceTarget.setStyle({ color: hoveredColor });
    }
  });

  polyline.on("mouseout", (e) => {
    e.sourceTarget.setStyle({ color });
  });

  return polyline;
};

const fitMapToPoints = (map, points) => {
  if (!points.length) return;

  map.fitBounds(points);
};

const addRoutePathListeners = ({
  layerGroup,
  routeId,
  routePathLayer,
  stopsMarkersLayer,
  encodedPath,
}) => {
  routePathLayer.on("click", (e) => {
    if (layerIsOnMap(e.sourceTarget._map, "PLANNED_ROUTE_STROKE")) {
      removeLayerFromMap(e.sourceTarget._map, "PLANNED_ROUTE_STROKE");

      stopsMarkersLayer.eachLayer((layer) => {
        if (layer._icon.classList.contains("greenmileMarkerIcon")) {
          const marker = layer._icon.querySelector(".markerRouteStops");

          marker?.classList?.remove("path-selected");

          window.globalStore.dispatch(actionCreators.removeFocusedRouteOnMap());
        }
      });

      return;
    }

    const stroke = L.polyline(encodedPath, {
      color: "#00FFFF",
      weight: 8,
    });
    stroke.kind = "PLANNED_ROUTE_STROKE";

    stroke.addTo(layerGroup);
    routePathLayer.bringToFront();

    stopsMarkersLayer.eachLayer((layer) => {
      if (layer._icon.classList.contains("greenmileMarkerIcon")) {
        const marker = layer._icon.querySelector(".markerRouteStops");

        marker?.classList?.add("path-selected");
      }
    });

    window.globalStore.dispatch(actionCreators.selectRouteOnMap({ routeId }));
  });
};

export const addRouteToMap = async ({ map, routeId, color }) => {
  const stops = await getStopsByRestrictions(routeId);
  const encodedPath = await getEncodedPath(routeId);

  const routePathLayer = getRoutePathLayer({ path: encodedPath, color });
  const { layer: stopsMarkersLayer, points } = generateStopMarkers({
    routeId,
    stops,
    color,
  });

  const routeLayer = L.layerGroup([routePathLayer, stopsMarkersLayer]);

  addRoutePathListeners({
    layerGroup: routeLayer,
    routeId,
    routePathLayer,
    stopsMarkersLayer,
    encodedPath,
  });

  routeLayer.kind = `ROUTE_PATH_${routeId}`;

  map.addLayer(routeLayer);
  fitMapToPoints(map, points);
};

export const removeLayerFromMap = (map, layerKind) => {
  map.eachLayer((item) => {
    if (item.kind === layerKind) {
      item.remove();
    }
  });
};

export const layerIsOnMap = (map, layerKind) => {
  let isOnMap = false;

  map.eachLayer((item) => {
    if (item.kind === layerKind) {
      isOnMap = true;
    }
  });

  return isOnMap;
};
